import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { Episode } from '../types/episodes';
import Picture from '../components/Picture';
import { Play, ArrowLeft } from 'lucide-react';

interface AllEpisodesPageProps {
  onNavigateToEpisode: (slug: string) => void;
  onBack: () => void;
}

export default function AllEpisodesPage({ onNavigateToEpisode, onBack }: AllEpisodesPageProps) {
  const [episodes, setEpisodes] = useState<Episode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadEpisodes();
  }, []);

  const loadEpisodes = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error: fetchError } = await supabase
        .from('episodes')
        .select('slug,title,summary,storage_root,game_thumbnail_460x215,podcast_number')
        .order('podcast_number', { ascending: true });

      if (fetchError) throw fetchError;
      setEpisodes((data || []) as Episode[]);
    } catch (err) {
      console.error('Error loading episodes:', err);
      setError(err instanceof Error ? err.message : 'Failed to load episodes');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-yellow-400 font-horizon text-2xl animate-pulse">Loading Episodes...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-red-500 text-xl">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <div className="fixed top-0 left-0 right-0 z-40 bg-black/95 backdrop-blur-sm border-b border-yellow-400/30">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-yellow-400 hover:text-yellow-300 group transition-colors"
          >
            <ArrowLeft className="group-hover:-translate-x-1 transition-transform" size={20} />
            <span className="font-bold">Back to Home</span>
          </button>
          <div className="text-yellow-400 font-press-start text-xs">
            All Episodes
          </div>
        </div>
      </div>

      <div className="pt-24 pb-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="font-horizon text-5xl md:text-7xl text-yellow-400 neon-glow mb-4">
              All Episodes
            </h1>
            <p className="text-gray-400 text-lg">
              Browse all {episodes.length} episodes of yOKzONa
            </p>
          </div>

          {episodes.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-400 text-xl">No episodes available yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" style={{ contentVisibility: "auto", containIntrinsicSize: "1000px" }}>
              {episodes.map((episode) => (
                <EpisodeGridCard
                  key={episode.slug}
                  episode={episode}
                  onClick={() => onNavigateToEpisode(episode.slug)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function EpisodeGridCard({ episode, onClick }: { episode: Episode; onClick: () => void }) {
  const storageRoot = episode.storage_root;

  return (
    <div
      onClick={onClick}
      className="bg-gradient-to-br from-purple-900/40 to-black border border-purple-600/50 rounded-lg overflow-hidden hover:border-yellow-400/70 hover:scale-105 transition-all duration-300 cursor-pointer group"
    >
      <div className="relative h-56 overflow-hidden">
        {episode.game_thumbnail_460x215 ? (
          <Picture
            storageRoot={storageRoot}
            filename={episode.game_thumbnail_460x215}
            alt={`${episode.title} thumbnail`}
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full bg-gray-800 flex items-center justify-center">
            <span className="text-gray-500">No Image</span>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-60" />
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <Play className="text-yellow-400" size={56} />
        </div>
        <div className="absolute top-3 left-3 bg-red-500 text-white px-3 py-1 text-xs font-bold">
          {episode.podcast_number}
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-press-start text-xs text-yellow-400 mb-2 line-clamp-2 leading-relaxed min-h-[2.5rem]">
          {episode.title}
        </h3>
        <p className="text-gray-400 text-sm line-clamp-3">
          {episode.summary || 'No summary available'}
        </p>
      </div>
    </div>
  );
}
